// What a cell holds once read: a number, text, TRUE or FALSE, an error, or nothing. What a person types is read here
// the way Google Sheets reads it: "1,200" and "$12.50" and "15%" are numbers (shown as typed), "2026-10-25" is a
// date, "TRUE" is true, and anything else is text. A date is a number of days, as in every spreadsheet: day 0 is
// 30 Dec 1899, so 1 Jan 1900 is day 2 and the files other spreadsheets write agree.

import type { ErrCode } from "./parse";

export class SheetErr {
  constructor(
    public code: ErrCode,
    /** what went wrong, in words, for the cell's hover and an agent's read */
    public msg = "",
  ) {}
  toString() {
    return this.code;
  }
}

export type Scalar = number | string | boolean | null | SheetErr;

export const isErr = (v: unknown): v is SheetErr => v instanceof SheetErr;
export const err = (code: ErrCode, msg = "") => new SheetErr(code, msg);

/** How a number was typed, so it is shown the same way until a format is chosen. */
export interface Implied {
  nf: "num" | "cur" | "pct" | "date" | "time" | "datetime";
  /** decimals as typed */
  dp?: number;
  /** a currency's symbol */
  sym?: string;
}

export interface Read {
  v: Scalar;
  implied?: Implied;
}

const DAY_MS = 86_400_000;
/** 30 Dec 1899 in ms since 1970, the day every spreadsheet counts from. */
const EPOCH = Date.UTC(1899, 11, 30);

export function serialOf(y: number, m: number, d: number): number {
  return Math.round((Date.UTC(y, m - 1, d) - EPOCH) / DAY_MS);
}

/** A day number to its year, month (1 to 12) and day. */
export function ymd(serial: number): { y: number; m: number; d: number } {
  const t = new Date(EPOCH + Math.floor(serial) * DAY_MS);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

/** 0 is Sunday. */
export function weekdayOf(serial: number): number {
  return new Date(EPOCH + Math.floor(serial) * DAY_MS).getUTCDay();
}

export function msToSerial(ms: number): number {
  return (ms - EPOCH) / DAY_MS;
}

/** A clock's now as a day number with its time, in a time zone (the user's), or as the computer's own. */
export function nowSerial(now: number, tz?: string | null): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz || undefined, year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric", hourCycle: "h23" }).formatToParts(new Date(now));
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    return serialOf(get("year"), get("month"), get("day")) + (get("hour") * 3600 + get("minute") * 60 + get("second")) / 86400;
  } catch {
    return msToSerial(now);
  }
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

function validDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || y < 1000 || y > 9999) return false;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

function fullYear(y: number, len: number): number {
  return len <= 2 ? (y < 50 ? 2000 + y : 1900 + y) : y;
}

/** A time typed as 9:30, 9:30:15, 9:30 PM or 9pm: its part of a day; null when it is not one. */
function timeOf(s: string): number | null {
  const m = /^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm|a|p)?$/i.exec(s.trim());
  if (!m || (m[2] === undefined && !m[4])) return null;
  let h = Number(m[1]);
  const min = Number(m[2] ?? 0);
  const sec = Number(m[3] ?? 0);
  if (min > 59 || sec > 59) return null;
  if (m[4]) {
    if (h < 1 || h > 12) return null;
    const pm = m[4].toLowerCase().startsWith("p");
    h = (h % 12) + (pm ? 12 : 0);
  } else if (h > 23) return null;
  return (h * 3600 + min * 60 + sec) / 86400;
}

/**
 * A date typed in a common way: 2026-10-25, 10/25/2026 (25/10/2026 when the sheet reads days first), Oct 25 2026, 25
 * Oct 2026. Numbers with slashes are read in the sheet's order: on a sheet that reads days first 03/04/2026 is 3 April,
 * on one that reads months first March 4. Only when that order gives no date (10/25/2026 day first) is the other tried,
 * so every date a sheet held before stays a date: only a date both ways (both numbers 12 or under) follows the order.
 */
function dateOf(s: string, dmy: boolean): number | null {
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return validDate(+m[1], +m[2], +m[3]) ? serialOf(+m[1], +m[2], +m[3]) : null;
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s);
  if (m) {
    const a = +m[1];
    const b = +m[2];
    const y = fullYear(+m[3], m[3].length);
    const [mo, d] = dmy ? [b, a] : [a, b];
    if (validDate(y, mo, d)) return serialOf(y, mo, d);
    return validDate(y, d, mo) ? serialOf(y, d, mo) : null;
  }
  m = /^([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/.exec(s);
  if (m) {
    const mo = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase()) + 1;
    return mo && validDate(+m[3], mo, +m[2]) ? serialOf(+m[3], mo, +m[2]) : null;
  }
  m = /^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})$/.exec(s);
  if (m) {
    const mo = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()) + 1;
    return mo && validDate(+m[3], mo, +m[1]) ? serialOf(+m[3], mo, +m[1]) : null;
  }
  return null;
}

const CURRENCY = /^([$£€¥₹])/;

/**
 * What a typed value is: its number, text, TRUE or FALSE, and how a number was typed. A formula is not read here
 * (shared/sheet/engine.ts works those out); "'" before anything keeps it as text, as in every spreadsheet.
 */
export function readInput(raw: string, dmy = false): Read {
  if (raw === "") return { v: null };
  if (raw.startsWith("'")) return { v: raw.slice(1) };
  const s = raw.trim();
  if (!s) return { v: raw };
  const up = s.toUpperCase();
  if (up === "TRUE" || up === "FALSE") return { v: up === "TRUE" };
  if (s.startsWith("#")) {
    const codes = ["#REF!", "#DIV/0!", "#NAME?", "#VALUE!", "#N/A", "#NUM!", "#NULL!"];
    if (codes.includes(up)) return { v: err(up as never) };
  }
  // numbers: 12, -3.5, 1,200, 1e6, (12) for -12, $12.50, -$5, 15%
  let t = s;
  let neg = false;
  if (/^\(.*\)$/.test(t)) {
    neg = true;
    t = t.slice(1, -1).trim();
  }
  if (t.startsWith("-")) {
    neg = !neg;
    t = t.slice(1).trim();
  } else if (t.startsWith("+")) t = t.slice(1).trim();
  let sym: string | undefined;
  const cm = CURRENCY.exec(t);
  if (cm) {
    sym = cm[1];
    t = t.slice(1).trim();
    if (t.startsWith("-")) {
      neg = !neg;
      t = t.slice(1).trim();
    }
  }
  let pct = false;
  if (t.endsWith("%")) {
    pct = true;
    t = t.slice(0, -1).trim();
  }
  const grouped = /^\d{1,3}(,\d{3})+(\.\d*)?$/.test(t);
  if (grouped || /^(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(t)) {
    const n = Number(t.replace(/,/g, ""));
    if (Number.isFinite(n)) {
      const dot = t.indexOf(".");
      const dp = dot >= 0 && !/e/i.test(t) ? t.length - dot - 1 : 0;
      const v = (neg ? -n : n) / (pct ? 100 : 1);
      if (pct) return { v, implied: { nf: "pct", dp } };
      // $12 shows as $12.00, as Sheets shows it
      if (sym) return { v, implied: { nf: "cur", dp: Math.max(2, dp), sym } };
      if (grouped) return { v, implied: { nf: "num", dp } };
      return { v };
    }
  }
  if (sym || pct) return { v: raw };
  // dates and times
  const d = dateOf(s, dmy);
  if (d !== null) return { v: d, implied: { nf: "date" } };
  const tm = timeOf(s);
  if (tm !== null) return { v: tm, implied: { nf: "time" } };
  const dt = /^(.+?)[ T](\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm)?)$/i.exec(s);
  if (dt) {
    const dd = dateOf(dt[1].trim(), dmy);
    const tt = timeOf(dt[2]);
    if (dd !== null && tt !== null) return { v: dd + tt, implied: { nf: "datetime" } };
  }
  return { v: raw };
}

/** Text as a number where it reads as one (a typed number, a date), as arithmetic takes it; null when it does not. */
export function textNumber(s: string, dmy = false): number | null {
  const r = readInput(s, dmy);
  return typeof r.v === "number" ? r.v : null;
}
